/**
 * @file 知识库文档按需加载（链路 A 交互 / 链路 B 编辑还原）
 * 仅导出两个 Hook；文件内共用 fetch 逻辑，不单独抽 useKbDocsLoader 层。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { clearCache, useMemoizedFn, useRequest } from 'ahooks';
import { compact, keyBy, uniq } from 'lodash-es';
import type { IKnowledgeBaseItem, IKnowledgeDocItem } from './useKnowledgeBaseApi';
import {
  fetchKnowledgeBaseDocs,
  filterKnowledgeBasesWithDocs,
  getKbDocsCacheKey,
  KB_DOCS_CACHE_TTL_MS,
  KB_DOCS_LOADING_DELAY_MS,
  mergeKbListWithDocs,
  pickNextInteractiveDocsKbId,
} from '../utils/knowledgeBaseUtils';

/** useRequest 返回值：绑定请求时的 kbId，避免 docsKbId 切换后错写文档 */
interface IKbDocsFetchResult {
  kbId: string;
  docs: IKnowledgeDocItem[];
}

/** 文件内共用：按 kbId 拉文档 + cacheKey，A/B 各自实例化 */
const useKbDocsCore = (
  kbList: IKnowledgeBaseItem[] | undefined,
  priorityKbId = '',
) => {
  const [docsByKbId, setDocsByKbId] = useState<Record<string, IKnowledgeDocItem[]>>({});
  const [docsKbId, setDocsKbId] = useState('');
  const [pendingKbIds, setPendingKbIds] = useState<string[]>([]);
  const [delayedLoadingKbId, setDelayedLoadingKbId] = useState('');
  const docsByKbIdRef = useRef(docsByKbId);

  docsByKbIdRef.current = docsByKbId;

  const kbMap = useMemo(
    () => keyBy(kbList || [], (item) => String(item.kbId)),
    [kbList],
  );

  const activeKb = docsKbId ? kbMap[docsKbId] : undefined;
  const canFetchDocs = Boolean(
    activeKb
    && ((activeKb.docCount ?? 0) > 0
      || Boolean(activeKb.docs?.length)),
  );

  const { data: kbDocsPayload, loading: docsLoading } = useRequest(
    async (): Promise<IKbDocsFetchResult> => {
      const requestKbId = docsKbId;
      const docs = await fetchKnowledgeBaseDocs(requestKbId, activeKb?.tenantId);
      return { kbId: requestKbId, docs };
    },
    {
      ready: Boolean(docsKbId) && canFetchDocs,
      refreshDeps: [docsKbId],
      cacheKey: docsKbId ? getKbDocsCacheKey(docsKbId) : undefined,
      staleTime: KB_DOCS_CACHE_TTL_MS,
      cacheTime: KB_DOCS_CACHE_TTL_MS,
    },
  );

  useEffect(() => {
    if (!kbDocsPayload) {
      return;
    }
    /** 兼容旧 cache（直接存数组） */
    const payload: IKbDocsFetchResult = Array.isArray(kbDocsPayload)
      ? { kbId: docsKbId, docs: kbDocsPayload }
      : kbDocsPayload;
    const { kbId, docs } = payload;
    if (!kbId) {
      return;
    }
    setDocsByKbId((prev) => (
      prev[kbId] !== undefined ? prev : { ...prev, [kbId]: docs }
    ));
  }, [kbDocsPayload, docsKbId]);

  const loadKbDocs = useMemoizedFn((kbIds: string[]): void => {
    const uniqueIds = uniq(compact((kbIds || []).map((id) => String(id))));
    if (!uniqueIds.length) {
      return;
    }
    uniqueIds.forEach((id) => {
      const kb = kbMap[id];
      if (!kb) {
        return;
      }
      if ((kb.docCount ?? 0) <= 0 && !kb.docs?.length) {
        setDocsByKbId((prev) => (
          prev[id] !== undefined ? prev : { ...prev, [id]: [] }
        ));
      }
    });
    setPendingKbIds((prev) => uniq([...prev, ...uniqueIds]));
  });

  useEffect(() => {
    const next = pickNextInteractiveDocsKbId(
      pendingKbIds,
      docsByKbId,
      kbMap,
      priorityKbId,
    );
    if (!next || docsLoading) {
      return;
    }
    if (next !== docsKbId) {
      setDocsKbId(next);
    }
  }, [pendingKbIds, docsByKbId, kbMap, docsKbId, priorityKbId, docsLoading]);

  /** UI 层 300ms 延迟：不依赖 ahooks docsLoading（cacheKey 下常为 false） */
  useEffect(() => {
    if (!docsKbId || docsByKbId[docsKbId] !== undefined) {
      setDelayedLoadingKbId('');
      return undefined;
    }
    const activeId = docsKbId;
    setDelayedLoadingKbId('');
    const timer = window.setTimeout(() => {
      if (docsByKbIdRef.current[activeId] === undefined) {
        setDelayedLoadingKbId(activeId);
      }
    }, KB_DOCS_LOADING_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [docsKbId, docsByKbId]);

  const mergedData = useMemo(
    () => filterKnowledgeBasesWithDocs(
      mergeKbListWithDocs(kbList || [], docsByKbId),
    ),
    [kbList, docsByKbId],
  );

  const isKbDocsLoaded = useMemoizedFn(
    (kbId: string): boolean => docsByKbId[String(kbId)] !== undefined,
  );

  const kbDocsLoadingIds = useMemo(
    () => pendingKbIds.filter((id) => docsByKbId[id] === undefined),
    [pendingKbIds, docsByKbId],
  );

  const isKbDocsLoading = useMemoizedFn((kbId: string): boolean => {
    const id = String(kbId);
    return kbDocsLoadingIds.includes(id);
  });

  /** 延迟 300ms 且文档尚未写入 docsByKbId 时才展示（当前正在拉的 docsKbId） */
  const kbDocsVisibleLoadingIds = useMemo(() => {
    if (!delayedLoadingKbId) {
      return [];
    }
    if (docsByKbId[delayedLoadingKbId] !== undefined) {
      return [];
    }
    return [delayedLoadingKbId];
  }, [delayedLoadingKbId, docsByKbId]);

  const clearKbDocsCache = useMemoizedFn((): void => {
    uniq([...pendingKbIds, ...Object.keys(docsByKbId)]).forEach((id) => {
      clearCache(getKbDocsCacheKey(id));
    });
    setPendingKbIds([]);
    setDocsKbId('');
    setDocsByKbId({});
    setDelayedLoadingKbId('');
  });

  return {
    loadKbDocs,
    mergedData,
    docsByKbId,
    isKbDocsLoaded,
    clearKbDocsCache,
    isKbDocsLoading,
    kbDocsLoadingIds,
    kbDocsVisibleLoadingIds,
    loadingDocsKbId: docsKbId,
  };
};

/**
 * 链路 A：面板 / @ 下拉交互（仅 loadKbDocs，由左列高亮库触发）
 * @param kbList - useKnowledgeBaseList 返回的原始列表
 * @param activeKbId - 面板当前高亮库，用于请求队列优先
 */
export const useInteractiveKbDocs = (
  kbList: IKnowledgeBaseItem[] | undefined,
  activeKbId = '',
) => {
  const core = useKbDocsCore(kbList, activeKbId);

  return {
    data: core.mergedData,
    docsByKbId: core.docsByKbId,
    loadKbDocs: core.loadKbDocs,
    clearKbDocsCache: core.clearKbDocsCache,
    isKbDocsLoading: core.isKbDocsLoading,
    kbDocsLoadingIds: core.kbDocsLoadingIds,
    kbDocsVisibleLoadingIds: core.kbDocsVisibleLoadingIds,
  };
};

export interface IUseHistoryKbDocsRestoreOptions {
  /** 知识库列表（原始列表） */
  kbList: IKnowledgeBaseItem[] | undefined;
  /** 历史文案里需要拉文档的 kbId（整库 @库名 不在此列） */
  restoreDocKbIds: string[];
  /** 是否启动还原 */
  ready: boolean;
}

/**
 * 链路 B：编辑态历史 mention 还原（独立实例，与面板 preload 分离）
 * @param options - 列表、待还原 id、就绪开关
 */
export const useHistoryKbDocsRestore = ({
  kbList,
  restoreDocKbIds,
  ready,
}: IUseHistoryKbDocsRestoreOptions) => {
  const core = useKbDocsCore(kbList);
  const restoreKey = restoreDocKbIds.join(',');

  useEffect(() => {
    if (!ready || !restoreKey) {
      return;
    }
    core.loadKbDocs(restoreDocKbIds);
  }, [ready, restoreKey, core.loadKbDocs, restoreDocKbIds]);

  const historyDocsReady = useMemo(() => {
    if (!restoreDocKbIds.length) {
      return true;
    }
    return restoreDocKbIds.every((id) => core.isKbDocsLoaded(id));
  }, [restoreDocKbIds, core.mergedData, core.isKbDocsLoaded]);

  return {
    data: core.mergedData,
    historyDocsReady,
  };
};
